import React from "react";
import { Box, Select, MenuItem } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import MultiQtyCell from "../../components/dms_common/MultiQtyCell";
import StatusDropdown from "../../components/dms_common/StatusDropdown";

interface ColumnOptions {
  disableEditing: boolean;
  handleRedirect?: (rowData: any) => void;
  handleOpenSubTable?: (rowId: string) => void;
  isSubTableOpen?: (rowId: string) => boolean;
  handleStatusChange?: (rowId: string, newStatus: string) => void;
  rowStatuses?: Record<string, string>;
  statusCellElement?: (row: any) => JSX.Element;
}

// Status mappings from DeliveryTable
const statusMappings = [
  {
    id: "DELIVERED",
    dmsLabelColor: "#029847",
    dmsLabel: "Delivered",
    dmsColor: "#EAFFF4",
  },
  {
    id: "CANCELLED",
    dmsLabelColor: "#C44C36",
    dmsLabel: "Cancelled",
    dmsColor: "#FFEFF0",
  },
  {
    id: "PARTIAL DELIVERED",
    dmsLabelColor: "#17B4A3",
    dmsLabel: "Partially Delivered",
    dmsColor: "#F0FBFF",
  },
];

export const getReturnDetailsColumnsMetaData = (options: ColumnOptions) => {
  const { 
    disableEditing,
    handleRedirect, 
    handleOpenSubTable, 
    isSubTableOpen, 
    statusCellElement,
    rowStatuses,
  } = options;
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const batchIdConfiguration = ConfigDictionary.batchIdConfiguration;
  const columns: any[] = [
    {
      headerName: "SKU Name & Code",
      mappedValue: "skuCode",
      width: "18%",
      renderCell: (row: any) => {
        return React.createElement("div", 
          { style: { display: "flex", flexDirection: "column" } },
          React.createElement("div", 
            { 
              style: { 
                overflow: "hidden", 
                textOverflow: "ellipsis", 
                width: "9rem", 
                whiteSpace: "nowrap" 
              }
            },
            row?.skuName
          ),
          React.createElement("div", 
            { style: { display: "flex", gap: "6px" } },
            React.createElement("div", null, row?.skuCode)
          )
        );
      }
    }
  ];

  if (batchIdConfiguration) {
    columns.push({
      headerName: "Batch ID",
      mappedValue: "batchId",
      width: "15%",
    });
  }

  columns.push(
    {
      headerName: "Total Qty",
      mappedValue: "totalCaseQty,totalPieceQty,totalOtherQty",
      width: "11%",
      displayType: "quantity",
    },
    {
      headerName: "Sold Qty",
      mappedValue: "soldCaseQty,soldPieceQty,soldOtherQty",
      width: "11%",
      displayType: "quantity",
    },
    {
      headerName: "Return Qty",
      mappedValue: "returnCaseQty,returnPieceQty,returnOtherQty",
      width: "15%",
      displayType: "quantity",
      hasEditPenSvg: !disableEditing, // Always show header edit pen icon when in edit mode
      redirectFunction: handleRedirect,
      skipRenderCondition: (row:any) => {console.log("ted ",rowStatuses?.[row.id]); return !(rowStatuses?.[row.id] === "PARTIAL DELIVERED") && (row.returnCaseQty == 0 && row.returnPieceQty == 0 && row.returnOtherQty == 0)},
      emptyCellWidth: "60%",
      showRedirectIconFunction: disableEditing ? (row:any) => false : (row:any) => rowStatuses?.[row.id] === "PARTIAL DELIVERED"
    },
    {
      headerName: "Status",
      mappedValue: "status",
      width: "15%",
      renderCell: statusCellElement
    }
  );

  // Only add Reason Details column if status is not INVOICED
  if ( handleOpenSubTable && isSubTableOpen) {
    columns.push({
      headerName: "Reason Details",
      mappedValue: "reasonDetails",
      width: "15%",
      skipRenderCondition: (row:any) => row.returnCaseQty == 0 && row.returnPieceQty == 0 && row.returnOtherQty == 0,
      emptyCellWidth: "60%",
      renderCell: (row: any) => {
        return React.createElement(Box, 
          {
            className: "batch-drowdown-container",
            onClick: () => handleOpenSubTable(row.id),
            sx: {
              cursor: "pointer",
              marginLeft: "auto",
              marginRight: "auto",
              backgroundColor: "#F7F7F7",
            }
          },
          React.createElement(Box, 
            { 
              sx: { 
                color: isSubTableOpen(row.id) ? "#17B4A3" : "#909090", 
                fontSize: "11px",
                whiteSpace: "nowrap"
              }
            },
            "View All"
          ),
          React.createElement("div", 
            { 
              className: "icon-border-circle-delivery",
              style: { borderColor: isSubTableOpen(row.id) ? "#17B4A3" : "#909090" }
            },
            isSubTableOpen(row.id) ? 
              React.createElement(KeyboardArrowUp, {
                style: {
                  fontSize: "11px",
                  color: isSubTableOpen(row.id) ? "#17B4A3" : "#909090",
                }
              }) :
              React.createElement(KeyboardArrowDown, { 
                style: { 
                  fontSize: "11px",
                  color: "#909090"
                } 
              })
          )
        );
      }
    });
  }
  
  // Redistribute widths if status is INVOICED (Reason Details column is hidden)
  if (!(handleOpenSubTable && isSubTableOpen)) {
    
    if (columns.length > 0) {
      // Calculate total percentage width
      const totalWidth = columns.reduce((sum, col) => {
        const width = parseFloat(col.width.replace("%", "") as string);
        return sum + width;
      }, 0);
      
      // Calculate how much width to redistribute (15% from Reason Details)
      const widthToRedistribute = 15;
      
      // Redistribute proportionally among existing columns
      columns.forEach(col => {
        const currentWidth = parseFloat(col.width.replace("%", "") as string);
        const proportion = currentWidth / totalWidth;
        const newWidth = currentWidth + (widthToRedistribute * proportion);
        col.width = `${Math.round(newWidth * 100) / 100}%`;
      });
    }
  }
  
  return columns;
};

export const subTableMetaData = {
  mappedValue: "reasonDetails",
  columns: [
    {
      headerName: "Returned Qty",
      mappedValue: "returnCaseQty,returnPieceQty,returnOtherQty",
      displayType: "quantity",
      width: "30%",
    },
    {
      headerName: "Type of Return", 
      mappedValue: "saleCondition",
      width: "30%",
    },
    {
      headerName: "Return Reason",
      mappedValue: "reason",
      width: "40%",
    },
    
  ]
};

export const invoiceDeliveryStatusMapping = [
  {
      id: "INVOICED",
      dmsLabelColor: "#CC911F",
      dmsLabel: "Pending",
      dmsColor: "#FFF9EF",
  },
  {
      id: "DELIVERED",
      dmsLabelColor: "#029847",
      dmsLabel: "Delivered",
      dmsColor: "#EAFFF4",
  },
  {
      id: "CANCELLED",
      dmsLabelColor: "#C44C36",
      dmsLabel: "Cancelled",
      dmsColor: "#FFEFF0",
  },
  {
      id: "PARTIAL DELIVERED",
      dmsLabelColor: "#17B4A3",
      dmsLabel: "Partially Delivered",
      dmsColor: "#F0FBFF",
  },
];